// OfflineBanner.tsx (Story 7.7 / UX-DR17) — the non-intrusive offline strip.
//
// A slim (≤32px) full-bleed strip at the very top of the app shell that appears ONLY when
// `isEffectivelyOnline() === false` (via `useEffectiveOnline()`). Tone is reassuring, not
// alarmist — offline is a SUPPORTED state (work is saved locally and syncs on reconnect), so it
// uses the muted `warning` token family, NEVER red/`danger` (clinical-UX no-alarm rule).
//
// There is no dedicated hi-fi mockup for this surface (docs/qa/epic-7-manual-test-scenarios.md
// accepts that) — it is built from the AC + design tokens. It mounts in `AppShell` so it survives
// an offline app-shell reload (7.2 precache) and is present app-wide.
//
// Built as a bespoke slim strip rather than the `Banner` card primitive: `Banner` is a padded,
// rounded card (px-16/py-12) that cannot meet the ≤32px full-bleed strip requirement. It still
// honours every token rule.

import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { useEffectiveOnline } from "@/lib/online-status";
import { cn } from "@/lib/utils";

/** Per-session dismissal flag — keyed to the CURRENT offline episode (cleared on reconnect). */
const DISMISS_KEY = "franklin:offline-banner-dismissed";

const OFFLINE_COPY =
  "You're offline — changes are saved on this device and will sync when you reconnect.";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "true";
  } catch {
    return false; // private mode / storage disabled — never crash the shell.
  }
}

export function OfflineBanner() {
  const online = useEffectiveOnline();
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  // Dismissal is keyed to the current offline EPISODE: when connectivity returns, clear the flag
  // so a later offline period (offline→online→offline within one session) re-shows the strip.
  useEffect(() => {
    if (!online) return;
    setDismissed(false);
    try {
      sessionStorage.removeItem(DISMISS_KEY);
    } catch {
      /* private mode — nothing to clear */
    }
  }, [online]);

  if (online || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      className={cn(
        "flex h-8 w-full items-center gap-2 px-4 text-xs font-medium",
        "bg-[color:var(--bg-warning-primary)] text-[color:var(--text-warning)]",
      )}
    >
      <span className="min-w-0 flex-1 truncate">{OFFLINE_COPY}</span>
      <button
        type="button"
        aria-label="Dismiss offline notice"
        onClick={() => {
          setDismissed(true);
          try {
            sessionStorage.setItem(DISMISS_KEY, "true");
          } catch {
            /* private mode — dismissal is best-effort */
          }
        }}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-[color:var(--bg-warning-secondary)]"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
